import { useToast } from '@/hooks/use-toast';
import { getEmployeeWorkDays } from '@/modules/time-off/my-toil/services/toil-eligibility.service';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useAuthStore } from '@/state/store/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  cancelLunchRequest,
  getLunchStatus,
  getMyLunchRequests,
  requestLunch,
} from '../services/lunch.service';
import { GetLunchRequestsParams } from '../types/lunch.types';

const LUNCH_STATUS_QUERY_KEY = 'hr-lunch-status';

export const useCurrentUserId = () => useAuthStore((state) => state.user?.itemId ?? '');

/**
 * The employee's working days on `day` ("YYYY-MM-DD"): the setting of `UserHRPeriodicalProfile.WorkDays`
 * in force that day in the HR term covering it, Monday-Friday when none is on file. Lunch can only be
 * requested for a day the employee actually works, so the request widget stays hidden on their weekend.
 */
export const useMyWorkDays = (day: string) => {
  const authUserId = useAuthStore((state) => state.user?.itemId ?? '');
  return useGlobalQuery({
    queryKey: ['hr-my-work-days', authUserId, day],
    queryFn: () => getEmployeeWorkDays(authUserId, day),
    enabled: Boolean(authUserId),
    staleTime: 30 * 60 * 1000,
  });
};

/** The lunch page's state for lunch day `day` ("YYYY-MM-DD"). */
export const useLunchStatus = (day: string, enabled = true, isWorkDay?: boolean) => {
  const authUserId = useAuthStore((state) => state.user?.itemId ?? '');
  const organizationId = useAuthStore((state) => state.selectedOrgId);
  return useGlobalQuery({
    queryKey: [LUNCH_STATUS_QUERY_KEY, authUserId, organizationId, day, isWorkDay],
    queryFn: () => getLunchStatus(authUserId, day, isWorkDay),
    enabled: enabled && Boolean(authUserId),
    // Status flips at the 11:00 cutoff and whenever menus change, so re-ask on every visit.
    staleTime: 0,
  });
};

export const useGetMyLunchRequests = (params: GetLunchRequestsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-my-lunch-requests', params],
    queryFn: () => getMyLunchRequests(params),
    staleTime: 0,
  });
};

export const useRequestLunch = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (lunchMenuId: string) => requestLunch(lunchMenuId),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: [LUNCH_STATUS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: ['hr-my-lunch-requests'] });
      // The workflow returns the translated result message key in `Data.MessageKey`.
      const key = result.Data?.MessageKey ?? 'LUNCH_REQUESTED';
      const params = result.Data?.MessageParams ?? {};
      toast({
        title: t(key, { ...params, defaultValue: result.Message || t('LUNCH_REQUESTED') }),
        variant: result.IsSuccessful ? 'default' : 'destructive',
      });
    },
  });
};

export const useCancelLunchRequest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => cancelLunchRequest(itemId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [LUNCH_STATUS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: ['hr-my-lunch-requests'] });
      toast({
        title: t('LUNCH_REQUEST_CANCELLED'),
      });
    },
  });
};
